
interface ProcessSectionProps {
  serviceType: 'drafting' | 'takeoff';
}

export default function ProcessSection({ serviceType }: ProcessSectionProps) {
  const draftingSteps = [
    {
      number: '01',
      title: 'Project Onboarding & Document Review',
      description: 'Upload your hand sketches, PDF redlines, and specific design parameters via our secure/complimentary industry partner portal.',
      icon: 'ri-discuss-line'
    },
    {
      number: '02',
      title: 'CAD Translation & Drafting',
      description: 'Our technicians translate your instructions into highly organized CAD files, utilizing strict industry-standard layer management.',
      icon: 'ri-draft-line'
    },
    {
      number: '03',
      title: 'Quality Assurance & Client Review',
      description: 'We provide initial drafts for your review, constant communication regarding your requirements, and ensure every dimension and markup has been captured exactly as requested.',
      icon: 'ri-refresh-line'
    },
    {
      number: '04',
      title: 'Final File Delivery',
      description: 'Receive fully editable files and clean PDF sets ready for your licensed professionals to review, stamp, and submit. Deliverables can be fully customized based on your needs — including CAD, DWG, RVT, PDF, or image formats.',
      icon: 'ri-file-check-line'
    }
  ];

  const takeoffSteps = [
    {
      number: '01',
      title: 'Document Submission',
      description: 'Upload your construction plans, specifications, and project details for our analysis.',
      icon: 'ri-upload-cloud-line'
    },
    {
      number: '02',
      title: 'Quantity Analysis',
      description: 'We perform detailed measurements and calculations for all materials, labor, and equipment.',
      icon: 'ri-calculator-line'
    },
    {
      number: '03',
      title: 'Cost Estimation',
      description: 'Apply current pricing, labor rates, and market conditions to generate accurate cost projections.',
      icon: 'ri-money-dollar-circle-line'
    },
    {
      number: '04',
      title: 'Report Delivery',
      description: 'Receive comprehensive takeoff reports with detailed breakdowns and budget recommendations.',
      icon: 'ri-file-list-3-line'
    }
  ];

  const steps = serviceType === 'drafting' ? draftingSteps : takeoffSteps;

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <span className="text-[#e18033] font-semibold tracking-wider uppercase mb-2 block">
            Our {serviceType === 'drafting' ? 'Drafting' : 'Takeoff'} Process
          </span>
          <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4">
            {serviceType === 'drafting' 
              ? 'A Streamlined, Client-Directed Workflow' 
              : 'Our Takeoff Process'}
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            {serviceType === 'drafting' 
              ? 'Designed for absolute precision, rapid turnaround times, and seamless integration with your internal review processes.' 
              : 'A streamlined workflow designed for efficiency, accuracy, and client satisfaction'}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {steps.map((step, index) => (
            <div key={index} className="relative">
              {index < steps.length - 1 && (
                <div className="hidden lg:block absolute top-16 left-full w-full h-0.5 bg-gradient-to-r from-[#e18033] to-orange-200 -translate-x-1/2 z-0"></div>
              )}
              
              <div className="relative z-10 text-center">
                <div className="w-32 h-32 flex items-center justify-center mx-auto mb-6 bg-gradient-to-br from-[#e18033] to-[#c97029] rounded-2xl shadow-lg">
                  <i className={`${step.icon} text-5xl text-white`}></i>
                </div>
                
                <div className="mb-3">
                  <span className="text-5xl font-bold text-[#e18033]/20">{step.number}</span>
                </div>
                
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {step.title}
                </h3>
                
                <p className="text-sm text-gray-600 leading-relaxed">
                  {step.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
